import { Injectable, OnDestroy, Optional } from '@angular/core';
import { BehaviorSubject, Observable, Subject, timer, firstValueFrom, Subscription, throwError, EMPTY, of } from 'rxjs';
import { takeUntil, tap, map, catchError, switchMap, filter, take } from 'rxjs/operators';
import { HubConnection, HubConnectionBuilder, LogLevel, HttpTransportType } from '@microsoft/signalr';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { NotificationDto } from '@models/notification.model';
import { environment } from '../../environments/environment';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { AuthTokenService } from './auth-token.service';
import { SecureStorageService } from './securestorage.service';
import { InteractionRequiredAuthError, InteractionStatus } from '@azure/msal-browser';

@Injectable({ providedIn: 'root' })
export class NotificationService implements OnDestroy {
    private hubConnection?: HubConnection;
    private readonly destroy$ = new Subject<void>();

    private readonly notificationsSubject = new BehaviorSubject<NotificationDto[]>([]);
    notifications$ = this.notificationsSubject.asObservable();

    private readonly unreadCountSubject = new BehaviorSubject<number>(0);
    unreadCount$ = this.unreadCountSubject.asObservable();

    private readonly totalCountSubject = new BehaviorSubject<number>(0);
    totalCount$ = this.totalCountSubject.asObservable();

    private readonly connectedSubject = new BehaviorSubject<boolean>(false);
    connected$ = this.connectedSubject.asObservable();

    // Polling fallback interval ms
    private readonly pollingInterval = 30000;
    private pollingSub?: Subscription;
    // module the active hub connection was opened with, so we can detect a switch
    private currentModule?: string;
    // true once the MSAL-gated initial connection attempt has run, so early module-change
    // events (eg. AppComponent persisting the route module on construction) can't race it
    private initialConnectionEstablished = false;

    constructor(private readonly http: HttpClient, private readonly msal: MsalService, private readonly authTokenService: AuthTokenService,
        private readonly storageService: SecureStorageService, @Optional() private readonly msalBroadcastService?: MsalBroadcastService) {
        // defer the initial fetch/SignalR start until MSAL has an authenticated account,
        // otherwise these fire pre-login on every homepage load and always fail with 401
        this.initializeOnceAuthenticated();
        this.subscribeToModuleChanges();
    }

    private hasAuthenticatedAccount(): boolean {
        return this.msal.instance.getAllAccounts().length > 0;
    }

    private initializeOnceAuthenticated(): void {
        if (!this.msalBroadcastService) {
            // no broadcast service available (e.g. unit tests) - preserve immediate behavior
            this.initialConnectionEstablished = true;
            this.syncUnreadCountSafely();
            this.startSignalR();
            this.startFallbackPolling();
            return;
        }

        // check account presence *within* the None filter so take(1) only completes once both
        // conditions hold - otherwise a pre-login None (no account yet) would consume the take(1)
        // and a later post-login None would never be observed
        this.msalBroadcastService.inProgress$
            .pipe(
                filter((status) => status === InteractionStatus.None && this.hasAuthenticatedAccount()),
                take(1),
                takeUntil(this.destroy$),
            )
            .subscribe(() => {
                this.initialConnectionEstablished = true;
                this.syncUnreadCountSafely();
                this.startSignalR();
                this.startFallbackPolling();
            });
    }

    // only starts once authenticated, and re-checks auth on every tick, so an
    // unauthenticated page left open can't trigger an UnreadCount call without a bearer token
    private startFallbackPolling(): void {
        timer(this.pollingInterval, this.pollingInterval)
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                if (this.hasAuthenticatedAccount() && (!this.hubConnection || this.connectedSubject.value === false)) {
                    this.syncUnreadCountSafely();
                }
            });
    }

    // HeaderComponent.toggleProject() can switch the active module without a page reload;
    // rebuild the hub so it stops using the stale X-Module/session context
    private subscribeToModuleChanges(): void {
        if (!this.storageService?.storageChange$) {
            return;
        }

        this.storageService.storageChange$
            .pipe(
                filter((change) => this.initialConnectionEstablished && change.key === 'activeModule' && change.value !== this.currentModule),
                takeUntil(this.destroy$),
            )
            .subscribe(() => this.restartSignalRForModuleChange());
    }

    private restartSignalRForModuleChange(): void {
        if (!this.hasAuthenticatedAccount()) {
            return;
        }

        const stop = this.hubConnection ? this.hubConnection.stop() : Promise.resolve();
        stop
            .catch((err) => console.warn('Error stopping SignalR connection before module switch', err))
            .finally(() => this.startSignalR());
    }

    private syncUnreadCountSafely(): void {
        this.getUnreadCount().pipe(
            takeUntil(this.destroy$),
            catchError((err) => {
                console.error('Background unread count sync failed', err);
                return EMPTY;
            })
        ).subscribe();
    }

    private async getAccessToken(): Promise<string | null> {
        // First, try to get a token via AuthTokenService which handles cache, in-flight requests
        try {
            const scopes = environment.msalConfig.auth.apiscopes as string[];
            const scopeKey = this.authTokenService.getScopeKey(scopes);
            const msalInstance: any = this.msal?.instance;
            const getAllAccounts = msalInstance?.getAllAccounts;
            const getActiveAccount = msalInstance?.getActiveAccount;
            const accounts = typeof getAllAccounts === 'function' ? getAllAccounts.call(msalInstance) : [];
            const activeAccount = typeof getActiveAccount === 'function' ? getActiveAccount.call(msalInstance) : undefined;
            const account = accounts && accounts.length ? accounts[0] : activeAccount ?? undefined;
            const silentRequest: any = { account, scopes };

            try {
                const token = await firstValueFrom(this.authTokenService.getToken(scopeKey, silentRequest as any, false));
                if (token) return token;
            } catch (e) {
                console.warn('AuthTokenService failed to acquire token silently', e);
            }

            // Fallback: try token from session storage (populated by AppComponent.generateToken())
            const stored = this.authTokenService.getTokenFromStorage(false) ?? this.storageService.getSessionStorageData('accessToken');
            if (stored) return stored;

            // As last resort, try MSAL interactive flow if required
            try {
                if (!msalInstance || typeof msalInstance.acquireTokenSilent !== 'function') {
                    return null;
                }
                const result = await msalInstance.acquireTokenSilent(silentRequest as any);
                return result?.accessToken ?? null;
            } catch (e) {
                console.warn('Could not acquire token silently for SignalR via MSAL instance', e);
                if (e instanceof InteractionRequiredAuthError) {
                    try {
                        if (typeof msalInstance?.acquireTokenPopup !== 'function') {
                            return null;
                        }
                        const resp = await msalInstance.acquireTokenPopup(silentRequest as any);
                        return resp?.accessToken ?? null;
                    } catch (ie) {
                        console.warn('Interactive token acquisition failed', ie);
                    }
                }
            }
            return null;
        } catch (err) {
            console.warn('getAccessToken unexpected error', err);
            return null;
        }
    }

    private startSignalR() {
        // build connection using hub url from environment
        const base = (environment as any).notificationHubUrl?.replace(/\/$/, '') ?? '';
        // SignalR's WebSocket transport can't set custom headers, so X-Module is sent as a query param
        // (SessionContextMiddleware falls back to it when the header is absent)
        const module = this.authTokenService.getActiveModule();
        this.currentModule = module;
        const hubUrl = `${base}/pushNotificationHub?X-Module=${encodeURIComponent(module)}`;
        // const hubUrl = (environment as any).notificationHubUrl ||
        //   (environment.notificationServiceUrl || '').replace(/\/api\/?$/, '') + 'notificationHub';
        // Build the connection and provide a dynamic accessTokenFactory so SignalR can request
        // a fresh token when it needs one (eg. reconnects)
        this.hubConnection = new HubConnectionBuilder()
            .withUrl(hubUrl, {
                accessTokenFactory: () => this.getAccessToken().then((t) => t ?? ''),
                transport: HttpTransportType.WebSockets | HttpTransportType.LongPolling,
                // auth is bearer-token based, not cookie based - avoid credentialed CORS requests
                // which the hub's AllowAnyOrigin() policy cannot satisfy (wildcard + credentials is blocked by browsers)
                withCredentials: false,
            })
            .configureLogging(LogLevel.Information)
            .withAutomaticReconnect()
            .build();

        this.hubConnection.onclose((err) => {
            console.warn('SignalR connection closed', err);
            this.connectedSubject.next(false);
        });

        this.hubConnection.onreconnecting((err) => {
            console.warn('SignalR reconnecting', err);
            this.connectedSubject.next(false);
        });

        this.hubConnection.onreconnected((connectionId) => {
            console.info('SignalR reconnected - connectionId:', connectionId);
            this.connectedSubject.next(true);
            // stop polling if any
            this.stopPolling();
        });

        // server will push unread count updates for all clients using 'UnreadCountUpdated'
        this.hubConnection.on('UnreadCountUpdated', (count: any) => {
            try {
                // Accept either a number or an object containing the count
                let value: number | null = null;
                if (typeof count === 'number') value = count;
                else if (count && typeof count === 'object') {
                    // common shapes: { count: 3 } or { unread: 3 }
                    if (typeof count.count === 'number') value = count.count;
                    else if (typeof count.unread === 'number') value = count.unread;
                } else if (typeof count === 'string' && !Number.isNaN(Number(count))) {
                    value = Number(count);
                }

                if (value !== null) {
                    this.unreadCountSubject.next(value);
                    console.info('UnreadCountUpdated received', value);
                } else {
                    console.warn('UnreadCountUpdated received unknown payload', count);
                }
            } catch (err) {
                console.warn('Error handling UnreadCountUpdated', err, count);
            }
        });

        // start connection
        this.hubConnection
            .start()
            .then(() => {
                console.info('SignalR connected');
                this.connectedSubject.next(true);
                // fetch history once connected
                // this.fetchLatest().subscribe();
            })
            .catch((err) => {
                console.warn('SignalR connection error', err);
                this.connectedSubject.next(false);
                // start polling fallback
                this.startPolling();
            });
    }

    private startPolling() {
        if (this.pollingSub) return;
        this.pollingSub = timer(0, this.pollingInterval)
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                if (!this.hubConnection || this.connectedSubject.value === false) {
                    // this.fetchLatest().subscribe();
                }
            });
    }

    private stopPolling() {
        if (this.pollingSub) {
            this.pollingSub.unsubscribe();
            this.pollingSub = undefined;
        }
    }

    /**
     * Call POST /FetchNotifications per swagger.
     * Accepts NotificationFetchCommand shape: { fetchAll?: boolean, isSuperAdmin?: boolean, limit?: number, emailId?: string }
     * Updates `notificationsSubject` and `unreadCountSubject` based on response.
     */
    fetchNotifications(options: { fetchAll?: boolean; isSuperAdmin?: boolean; limit?: number; emailId?: string } = {}): Observable<any> {
        const base = (environment.notificationServiceUrl || '').replace(/\/$/, '');
        const url = `${base}/PushNotification/FetchNotifications`;
        const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
        return this.http.post<any>(url, options, { headers }).pipe(
            tap((resp) => {
                const result = resp?.data ?? resp;
                if (typeof result?.combinedUnread === 'number') {
                    this.unreadCountSubject.next(result.combinedUnread);
                }
                this.notificationsSubject.next(result.notifications ?? []);

                if (typeof result?.totalNotificationCount === 'number') {
                    this.totalCountSubject.next(result.totalNotificationCount);
                }
            }),
            catchError((err) => {
                console.error('fetchNotifications failed', { url, options, error: err });
                return throwError(() => err);
            }),
            map((resp) => resp?.data ?? resp)
        );
    }


    markAllRead(): Observable<any> {
        const base = (environment.notificationServiceUrl || '').replace(/\/$/, '');
        const url = `${base}/PushNotification/MarkAllAsRead`;
        return this.http.patch<any>(url, {}).pipe(
            tap(() => {
                const list = this.notificationsSubject.value.map((n) => ({ ...n, isRead: true }));
                this.notificationsSubject.next(list);
                this.unreadCountSubject.next(0);
            }),
            catchError((err) => {
                console.error('markAllRead failed', { url, error: err });
                return throwError(() => err);
            })
        );
    }

    /**
     * Calls PATCH /PushNotification/MarkAsRead/{id} per swagger.
     * Updates local notification list and unread count.
     */
    markAsRead(id: string): Observable<any> {
        const base = (environment.notificationServiceUrl || '').replace(/\/$/, '');
        const url = `${base}/PushNotification/MarkAsRead/${id}`;

        return this.http.patch<any>(url, {}, {}).pipe(
            switchMap((response) => {
                const currentList = this.notificationsSubject.value;
                const target = currentList.find((n) => n.id === id);
                const list = currentList.map((n) => n.id === id ? { ...n, isRead: true } : n);
                this.notificationsSubject.next(list);

                // Do not derive unread count from the current page; it can be a partial list.
                if (target && !target.isRead) {
                    const unread = Math.max(0, this.unreadCountSubject.value - 1);
                    this.unreadCountSubject.next(unread);
                }

                if (!target) {
                    return this.getUnreadCount().pipe(
                        map(() => response),
                        catchError((err) => {
                            console.error('Unread count sync failed after markAsRead', { url, id, error: err });
                            return of(response);
                        })
                    );
                }

                return of(response);
            }),
            catchError((err) => {
                console.error('markAsRead failed', { url, id, error: err });
                return throwError(() => err);
            })
        );
    }

    /**
     * Calls DELETE /PushNotification/Delete/{id} per swagger.
     * Removes notification from local list and updates unread count.
     */
    deleteNotification(id: string, totalCount: number): Observable<any> {
        const base = (environment.notificationServiceUrl || '').replace(/\/$/, '');
        const url = `${base}/PushNotification/Delete/${id}`;

        return this.http.delete<any>(url).pipe(
            switchMap((response) => {
                const currentList = this.notificationsSubject.value;
                const target = currentList.find((n) => n.id === id);
                const list = currentList.filter((n) => n.id !== id);
                this.notificationsSubject.next(list);

                // Do not derive unread count from the current page; it can be a partial list.
                if (target && !target.isRead) {
                    const unread = Math.max(0, this.unreadCountSubject.value - 1);
                    this.unreadCountSubject.next(unread);
                }

                this.totalCountSubject.next(totalCount - 1);

                if (!target) {
                    return this.getUnreadCount().pipe(
                        map(() => response),
                        catchError((err) => {
                            console.error('Unread count sync failed after deleteNotification', { url, id, error: err });
                            return of(response);
                        })
                    );
                }

                return of(response);
            }),
            catchError((err) => {
                console.error('deleteNotification failed', { url, id, error: err });
                return throwError(() => err);
            })
        );
    }

    /**
     * Calls DELETE /PushNotification/DeleteAll per swagger.
     * Clears local notification list and unread count.
     */
    deleteAllNotifications(): Observable<any> {
        const base = (environment.notificationServiceUrl || '').replace(/\/$/, '');
        const url = `${base}/PushNotification/DeleteAll`;

        return this.http.delete<any>(url).pipe(
            tap(() => {
                this.notificationsSubject.next([]);
                this.unreadCountSubject.next(0);
                this.totalCountSubject.next(0);
            }),
            catchError((err) => {
                console.error('deleteAllNotifications failed', { url, error: err });
                return throwError(() => err);
            })
        );
    }

    /**
     * Calls GET /PushNotification/UnreadCount per swagger.
     * Updates unreadCountSubject with the returned count.
     */
    getUnreadCount(): Observable<number> {
        const base = (environment.notificationServiceUrl || '').replace(/\/$/, '');
        const url = `${base}/PushNotification/UnreadCount`;
        return this.http.get<any>(url).pipe(
            map((resp) => {
                // response may be { data: number } or a plain number
                const val = resp?.data ?? resp;
                const count = typeof val === 'number' ? val : Number(val) || 0;
                this.unreadCountSubject.next(count);
                return count;
            }),
            catchError((err) => {
                console.error('getUnreadCount failed', { url, error: err });
                return throwError(() => err);
            })
        );
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        if (this.hubConnection) {
            this.hubConnection.stop();
        }
    }
}
